import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Shell } from "@/components/Shell";
import { SECTIONS, CASALE } from "@/data/questionnaire";
import { STAFF_QUESTIONS, STAFF_ROLES } from "@/data/staff";

const Box = () => <span className="inline-block w-5 h-5 border-2 border-[#8A7970] rounded-[4px] align-middle mr-3 flex-none" />;
const Lines = ({ n = 3 }) => (
  <div className="grid gap-5 mt-4">{Array.from({ length: n }).map((_, i) => <div key={i} className="border-b border-[#CDBDA7]" />)}</div>
);

const Blank = ({ q }) => {
  if (q.type === "number") return <p className="mt-3 text-[#5C4D46]">→ circa <span className="inline-block w-28 border-b border-[#8A7970] align-baseline" /> {q.unit}</p>;
  if (q.type === "text") return <Lines />;
  if (q.type === "scale")
    return (
      <div className="flex items-center gap-4 mt-3 text-[#5C4D46] flex-wrap">
        <span className="text-sm">{q.lowLabel}</span>
        {Array.from({ length: q.max - q.min + 1 }, (_, i) => q.min + i).map((n) => (
          <span key={n} className="flex items-center"><Box />{n}</span>
        ))}
        <span className="text-sm">{q.highLabel}</span>
      </div>
    );
  return (
    <ul className="grid gap-2.5 mt-3 sm:grid-cols-2">
      {q.options.map((o) => <li key={o} className="flex items-start text-[#2C221E]"><Box />{o}</li>)}
    </ul>
  );
};

export default function BlankFormPage() {
  const navigate = useNavigate();
  let n = 0;
  return (
    <Shell wide header={
      <div className="flex gap-3 no-print">
        <button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} data-testid="blank-back-btn"><ArrowLeft size={16} /> Indietro</button>
        <button className="btn btn-olive !min-h-[44px] text-sm" onClick={() => window.print()} data-testid="blank-print-btn"><Printer size={16} /> Stampa / PDF</button>
      </div>
    }>
      <div className="fade-up" data-testid="blank-form">
        <p className="eyebrow">Modulo da compilare</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">{CASALE.name}</h1>
        <div className="flex flex-wrap gap-x-10 gap-y-4 mt-6 text-[#5C4D46]">
          <p>Chi risponde: <span className="inline-block w-64 border-b border-[#8A7970]" /></p>
          <p>Data: <span className="inline-block w-40 border-b border-[#8A7970]" /></p>
        </div>

        <div className="grid gap-8 mt-10">
          {SECTIONS.map((s, si) => (
            <section key={s.id} className="card p-6 sm:p-9" data-testid={`blank-section-${s.id}`}>
              <p className="eyebrow">Sezione {si + 1}</p>
              <h2 className="font-serif text-3xl font-semibold text-[#2D4030] mt-1">{s.title}</h2>
              <p className="text-[#7A6B63] mt-1 mb-6">{s.intro}</p>
              <div className="grid gap-8">
                {s.questions.map((q) => {
                  n += 1;
                  return (
                    <div key={q.id} className="break-inside-avoid">
                      <p className="text-lg font-semibold text-[#2C221E] leading-snug">
                        <span className="font-serif text-[#C85A32] mr-2">{n}.</span>{q.text}
                        {q.type === "multiple" && <span className="text-sm font-normal text-[#7A6B63]"> (più risposte)</span>}
                        {q.type === "single" && <span className="text-sm font-normal text-[#7A6B63]"> (una sola risposta)</span>}
                        {q.showIf && <span className="text-sm font-normal italic text-[#7A6B63]"> — saltare se lo chef resta</span>}
                      </p>
                      <Blank q={q} />
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        <p className="font-serif text-2xl text-center text-[#2D4030] mt-12">Grazie per il tempo dedicato.</p>

        <section className="card p-6 sm:p-9 mt-12 print-break" data-testid="blank-staff-section">
          <p className="eyebrow">Allegato</p>
          <h2 className="font-serif text-3xl font-semibold text-[#2D4030] mt-1">Voce dello staff</h2>
          <p className="text-[#7A6B63] mt-1 mb-5">Da far compilare a una persona di sala o cucina, separatamente dai titolari.</p>
          <div className="flex flex-wrap gap-x-10 gap-y-3 mb-6 text-[#5C4D46]">
            <p>Nome: <span className="inline-block w-48 border-b border-[#8A7970]" /></p>
            <p className="flex flex-wrap items-center gap-x-4 gap-y-2">Ruolo: {STAFF_ROLES.map((r) => <span key={r} className="flex items-center"><Box />{r}</span>)}</p>
          </div>
          <div className="grid gap-8">
            {STAFF_QUESTIONS.map((q, i) => (
              <div key={q.id} className="break-inside-avoid">
                <p className="text-lg font-semibold text-[#2C221E] leading-snug">
                  <span className="font-serif text-[#C85A32] mr-2">{i + 1}.</span>{q.text}
                  {q.type === "multiple" && <span className="text-sm font-normal text-[#7A6B63]"> (più risposte)</span>}
                  {q.type === "single" && <span className="text-sm font-normal text-[#7A6B63]"> (una sola risposta)</span>}
                </p>
                <Blank q={q} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </Shell>
  );
}
